import { parseApiError } from '../utils/apiErrorParser';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Helper function to get API role from UI role
 * @param {string} uiRole - UI role (SuperAdmin, Ops, Finance, Support)
 * @returns {string} API role (superadmin, ops, finance, support)
 */
const getApiRole = (uiRole) => {
  const roleMapping = {
    'SuperAdmin': 'superadmin',
    'Finance': 'finance',
    'Ops': 'ops',
    'Support': 'support'
  };
  return roleMapping[uiRole] || 'ops';
};

/**
 * Helper function to make API requests with authentication
 * @param {string} endpoint - API endpoint (relative to BASE_URL)
 * @param {object} options - Request options (method, body, headers)
 * @returns {Promise<object>} API response data
 */
const makeRequest = async (endpoint, options = {}) => {
  try {
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      throw new Error('No authentication token found. Please login first.');
    }
    
    const fullUrl = `${BASE_URL}${endpoint}`;
    
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    const requestOptions = {
      method: options.method || 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      signal: controller.signal
    };
    
    // Include body only for POST, PUT, PATCH requests
    if (options.body && ['POST', 'PUT', 'PATCH'].includes(requestOptions.method)) {
      requestOptions.body = options.body;
    }
    
    const response = await fetch(fullUrl, requestOptions);
    
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      let message = '';
      try {
        message = parseApiError(JSON.parse(errorText));
      } catch {
        message = errorText || `Request failed (${response.status})`;
      }
      const error = new Error(message);
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - backend may not be running');
    }
    
    throw error;
  }
};

/**
 * Add a new medicine to the service catalog
 * 
 * @param {string} role - User role (Ops or SuperAdmin)
 * @param {object} medicineData - Medicine data to add
 * @param {string} medicineData.service_item_name - Medicine name (required)
 * @param {string} medicineData.vendor_id - Vendor UUID (required)
 * @param {number} medicineData.partner_price - MRP (required, >= 0)
 * @param {number} medicineData.partner_discount - AyushPay MRP (required, >= 0, <= partner_price)
 * @param {string} [medicineData.service_item_id] - Custom ID (optional, auto-generated if not provided)
 * @returns {Promise<object>} API response with created medicine item
 * @throws {Error} If role is not authorized or API call fails
 * 
 * @example
 * const medicine = await addMedicineToCatalog('Ops', {
 *   service_item_name: 'Paracetamol 500mg',
 *   vendor_id: '62312587-03cf-4270-aa26-83d0e7a62794',
 *   partner_price: 50.0,
 *   partner_discount: 45.0
 * });
 */
export const addMedicineToCatalog = async (role, medicineData) => {
  // Validate role authorization
  if (role !== 'Ops' && role !== 'SuperAdmin') {
    throw new Error('Unauthorized: Only Ops and SuperAdmin can add medicines to catalog');
  }
  
  // Validate required fields
  if (!medicineData.service_item_name?.trim()) {
    throw new Error('service_item_name is required');
  }
  
  if (!medicineData.vendor_id) {
    throw new Error('vendor_id is required');
  }
  
  if (medicineData.partner_price === undefined || medicineData.partner_price === null) {
    throw new Error('partner_price is required');
  }
  
  if (medicineData.partner_discount === undefined || medicineData.partner_discount === null) {
    throw new Error('partner_discount is required');
  }
  
  // Validate business logic
  if (medicineData.partner_discount > medicineData.partner_price) {
    throw new Error('partner_discount cannot exceed partner_price');
  }
  
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/medicine/add/`;
  
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(medicineData)
  });
};

/**
 * Get service providers list for medicine management
 * 
 * @param {string} role - User role (Ops or SuperAdmin)
 * @param {string} serviceType - Service type (default: 'medicine')
 * @returns {Promise<object>} API response with providers list
 */
export const getMedicineProviders = async (role, serviceType = 'medicine') => {
  if (role !== 'Ops' && role !== 'SuperAdmin') {
    throw new Error('Unauthorized: Only Ops and SuperAdmin can access medicine providers');
  }
  
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/providers/list/?service_type=${serviceType}`;
  
  return makeRequest(endpoint);
};

/**
 * List medicines for a vendor
 * 
 * @param {string} role - User role (Ops or SuperAdmin)
 * @param {string} vendorId - Vendor UUID (required)
 * @param {object} params - Query parameters
 * @param {string} params.search - Search term (optional)
 * @param {number} params.page - Page number (optional, default: 1)
 * @param {number} params.items - Items per page (optional, default: 10)
 * @returns {Promise<object>} API response with medicines list and pagination
 */
export const listMedicines = async (role, vendorId, params = {}) => {
  if (role !== 'Ops' && role !== 'SuperAdmin') {
    throw new Error('Unauthorized: Only Ops and SuperAdmin can list medicines');
  }
  
  if (!vendorId) {
    throw new Error('vendor_id is required');
  }
  
  const apiRole = getApiRole(role);
  const queryParams = new URLSearchParams({
    vendor_id: vendorId
  });
  
  if (params.search) {
    queryParams.append('search', params.search);
  }
  if (params.page) {
    queryParams.append('page', params.page.toString());
  }
  if (params.items) {
    queryParams.append('items', params.items.toString());
  }
  
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/medicine/list/?${queryParams.toString()}`;
  
  return makeRequest(endpoint);
};

/**
 * Edit medicine details
 * 
 * @param {string} role - User role (Ops or SuperAdmin)
 * @param {string} medicineId - Medicine UUID (required)
 * @param {object} medicineData - Medicine data to update (partial update supported)
 * @param {string} [medicineData.service_item_name] - Medicine name
 * @param {number} [medicineData.partner_price] - MRP
 * @param {number} [medicineData.partner_discount] - AyushPay MRP
 * @param {number} [medicineData.unit] - Unit
 * @param {boolean} [medicineData.active] - Active status
 * @returns {Promise<object>} API response with updated medicine item
 */
export const editMedicine = async (role, medicineId, medicineData) => {
  if (role !== 'Ops' && role !== 'SuperAdmin') {
    throw new Error('Unauthorized: Only Ops and SuperAdmin can edit medicines');
  }
  
  if (!medicineId) {
    throw new Error('medicine_id is required');
  }
  
  // Validate business logic if both price and discount are provided
  if (medicineData.partner_price !== undefined && medicineData.partner_discount !== undefined) {
    if (medicineData.partner_discount > medicineData.partner_price) {
      throw new Error('partner_discount cannot exceed partner_price');
    }
  }
  
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/medicine/${medicineId}/edit/`;
  
  return makeRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify(medicineData)
  });
};

